/**
 * O painel "Mais filtros", compartilhado entre a tela de Menções e o
 * formulário de geração do relatório (2026-10-17).
 *
 * Extraído da `MentionsPage` sem mudar o comportamento de menções. As
 * duas telas leem o MESMO filtro (a loja da casca, via
 * `useMentionFilters`), então o painel é um só: o mesmo rascunho, o
 * mesmo "Aplicar", os mesmos controles.
 *
 * **O painel edita um RASCUNHO** (ver `filter-draft.test.ts`, que lê
 * ESTE arquivo): nenhum controle escreve no estado vivo, e só o
 * "Aplicar" o promove. Os nomes abaixo vêm todos da desestruturação do
 * hook — o teste de fonte procura `setFilters(`, `filters.` etc. dentro
 * do `<aside>` e não pode achar nenhum.
 *
 * `mode="report"` tira o que o relatório não leva ou que ele resolve em
 * outro lugar: o CENA mínimo (a rede só ganha nota durante a geração),
 * "Editado por" e "Edição" (atributos da trilha, não da menção), "Só as
 * descartadas" (menção descartada nunca entra em relatório) e o grupo
 * "Quando" (no relatório a janela fica na linha do formulário, viva,
 * fora do rascunho). Revisão fica: viaja pro relatório.
 *
 * **Alfinetes (2026-10-30)**: na tela de Menções (`pins`), cada filtro
 * tem um alfinete que o fixa na barra de filtros rápidos
 * (`quick-filters.ts`). Por isso a Busca e a Polaridade, que moram na
 * barra, aparecem aqui também nesse modo — sem elas, quem as soltasse
 * não teria de onde fixá-las de volta. A Polaridade edita o rascunho como
 * as outras facetas; a Busca escreve na busca viva, como a da barra (a
 * busca nunca foi parte do rascunho).
 */

import { Calendar, Check, CircleDollarSign, FileSpreadsheet, Gauge, Link2, Pin, Search, X } from "lucide-react";
import type { ReactNode } from "react";
import type { UploadSummary } from "@/shared/api/api-client";
import {
  DIMENSION_LABELS,
  type Dimension,
} from "@/features/mentions/mention-filter-criteria";
import { uploadLabel } from "@/features/uploads/upload-shared";
import { MultiSelectFilter } from "@/shared/components/MultiSelectFilter";
import { Select } from "@/shared/components/Select";
import { ToolbarSelect } from "@/shared/components/ToolbarSelect";
import {
  MENTION_SEARCH_FIELDS,
  MENTION_SEARCH_FIELD_LABELS,
  type MentionSearchField,
} from "@/shared/vocabulary/mention-search-fields";
import type { useMentionFilters } from "@/features/mentions/use-mention-filters";
import { quickFilterLabel, type QuickFilterKey } from "@/features/mentions/quick-filters";

/** Aviso das dimensões que só existem em METADE das menções. Desde
 *  2026-08-27 elas recortam só essa metade e deixam a outra passar —
 *  antes apagavam a outra metade do recorte. */
const HALF_SCOPE_HINTS: Partial<Record<Dimension, string>> = {
  tiers: "Só imprensa: recorta a imprensa e deixa as menções de redes passarem.",
  formats: "Só imprensa: recorta a imprensa e deixa as menções de redes passarem.",
  influencerTypes: "Só redes: recorta as redes e deixa as menções de imprensa passarem.",
  publisherGroups: "Só redes: recorta as redes e deixa as menções de imprensa passarem.",
  clippingServices:
    "A clipadora do upload de onde a menção veio. \"Legado (migrado)\" são as menções trazidas do sistema antigo, que não têm upload.",
  mediaTypes:
    "Só imprensa: recorta a imprensa e deixa as menções de redes passarem. Vem da coluna da clipadora e, sem ela, do cadastro do veículo.",
};

/** O alfinete de cada filtro (2026-10-30): fixado = aparece na barra de
 *  filtros rápidos da tela de Menções. Só a tela de Menções o passa; o
 *  formulário do relatório não tem barra pra fixar. */
export type FilterPins = {
  pinned: readonly QuickFilterKey[];
  onToggle: (key: QuickFilterKey) => void;
  onRestoreDefault: () => void;
  isDefault: boolean;
};

function PinButton({ pinned, label, onToggle }: { pinned: boolean; label: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`filter-pin ${pinned ? "is-pinned" : ""}`}
      aria-pressed={pinned}
      aria-label={pinned ? `Tirar ${label} da barra` : `Fixar ${label} na barra`}
      title={pinned ? "Fixado na barra — clique para tirar" : "Fixar na barra"}
      onClick={onToggle}
    >
      <Pin size={14} aria-hidden="true" />
    </button>
  );
}

type MentionFilterPanelProps = {
  /** O objeto inteiro de `useMentionFilters` — o painel não tem estado
   *  próprio. */
  hook: ReturnType<typeof useMentionFilters>;
  mode: "mentions" | "report";
  showsCena: boolean;
  /** O texto pequeno do cabeçalho: "MENÇÕES" / "RELATÓRIO". */
  eyebrow: string;
  uploadOptions: UploadSummary[];
  uploadFilter: number[];
  onChangeUploadFilter?: (uploadIds: number[]) => void;
  className?: string;
  pins?: FilterPins;
};

export function MentionFilterPanel({
  hook,
  mode,
  showsCena,
  eyebrow,
  uploadOptions,
  uploadFilter,
  onChangeUploadFilter,
  className = "",
  pins,
}: MentionFilterPanelProps) {
  const {
    draft,
    setDraft,
    draftFacet,
    setDraftFacetExclude,
    toggleDraftFacet,
    clearDraftFacet,
    clearFilters,
    optionsFor,
    filterOptions,
    retryFilterOptions,
    channelsHint,
    vehiclesHint,
    searchVehicles,
    searchChannels,
    closePanel,
    applyDraft,
    draftDiffers,
    activeFilterCount,
    refFilter,
    refDraft,
    setRefDraft,
    refBusy,
    refError,
    applyPastedReferences,
    clearPastedReferences,
    query,
    setQuery,
    searchIn,
    setSearchIn,
  } = hook;

  /** O alfinete de um filtro, ou nada fora da tela de Menções. */
  const pin = (key: QuickFilterKey) =>
    pins ? (
      <PinButton
        pinned={pins.pinned.includes(key)}
        label={quickFilterLabel(key)}
        onToggle={() => pins.onToggle(key)}
      />
    ) : null;
  /** Um controle inteiro com o alfinete ao lado. */
  const pinnable = (key: QuickFilterKey, control: ReactNode) =>
    pins ? (
      <div className="pinnable-filter" key={key}>
        {control}
        {pin(key)}
      </div>
    ) : (
      control
    );

  // Canal e Veículo têm catálogo grande e buscam no servidor; os hints
  // vêm do hook (dizem o meio-escopo de cada um).
  const hintFor = (dimension: Dimension) =>
    dimension === "channels"
      ? channelsHint
      : dimension === "vehicles"
        ? vehiclesHint
        : HALF_SCOPE_HINTS[dimension];
  const searchFor = (dimension: Dimension) =>
    dimension === "channels"
      ? searchChannels
      : dimension === "vehicles"
        ? searchVehicles
        : undefined;

  /** Uma dimensão multi-seleção do painel: lê e escreve o RASCUNHO. */
  const facetFilter = (dimension: Dimension, hint = hintFor(dimension)) =>
    pinnable(
      dimension,
      <MultiSelectFilter
        key={dimension}
        label={DIMENSION_LABELS[dimension]}
        hint={hint}
        search={searchFor(dimension)}
        options={optionsFor(dimension)}
        selected={draftFacet(dimension).values}
        exclude={draftFacet(dimension).exclude}
        onExcludeChange={(value) => setDraftFacetExclude(dimension, value)}
        onToggle={(value) => toggleDraftFacet(dimension, value)}
        onClear={() => clearDraftFacet(dimension)}
        // Clipadora é inclusão pura: o servidor não exclui por ela.
        excludable={dimension !== "clippingServices"}
      />,
    );

  return (
    <aside className={`drawer filters-panel ${className}`.trim()}>
      <div className="drawer-head">
        <div>
          <span>{eyebrow}</span>
          <strong>Mais filtros</strong>
        </div>
        <button
          className="icon-button"
          aria-label="Fechar filtros"
          onClick={closePanel}
        >
          <X size={19} />
        </button>
      </div>
      <div className="drawer-body">
        {/* Filtro que não carregou precisa APARECER. Antes, uma rota
            de catálogo fora do ar devolvia lista vazia e a tela dizia
            "não há temas" — indistinguível de catálogo vazio. Com a
            cerca de requisições isso deixou de ser hipótese: abrir
            este painel dispara seis chamadas de uma vez. */}
        {filterOptions.failed.length > 0 && (
          <div className="filter-load-warning" role="status">
            <span>
              Não foi possível carregar:{" "}
              {filterOptions.failed.join(", ")}. Os demais filtros
              funcionam normalmente.
            </span>
            <button
              type="button"
              className="link-button"
              onClick={retryFilterOptions}
            >
              Tentar de novo
            </button>
          </div>
        )}
        {pins && (
          <div className="filter-pin-intro">
            <p>
              <Pin size={13} aria-hidden="true" /> fixa o filtro na barra da tela. Lá, é só
              segurar e arrastar o filtro pra mudar a ordem (no teclado, Alt + setas).
            </p>
            <button
              type="button"
              className="text-button"
              disabled={pins.isDefault}
              onClick={pins.onRestoreDefault}
            >
              Restaurar barra padrão
            </button>
          </div>
        )}
        {/* A busca mora na barra por padrão; aqui ela existe pra poder ser
            solta e fixada de novo (2026-10-30). Escreve na busca VIVA, como
            a da barra: a busca nunca foi parte do rascunho (tem debounce
            próprio). */}
        {mode === "mentions" && (
          <div className="filter-group">
            <h4 className="filter-group-title">Busca</h4>
            <div className="filter-section">
              <div className="filter-section-head">
                <Search size={15} />
                <span>Título ou conteúdo</span>
                {pin("search")}
              </div>
              <div className="drawer-fields filter-search-fields">
                <input
                  name="mention-search-panel"
                  autoComplete="off"
                  aria-label="Buscar menções no painel"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por título ou conteúdo…"
                  title="Aceita AND, OR, NOT e parênteses. Aspas = palavra ou frase inteira. Sem operador entre palavras vale AND."
                />
                <Select
                  aria-label="Buscar em (painel)"
                  value={searchIn}
                  onChange={(event) =>
                    setSearchIn(event.target.value as MentionSearchField)
                  }
                >
                  {MENTION_SEARCH_FIELDS.map((field) => (
                    <option key={field} value={field}>
                      {MENTION_SEARCH_FIELD_LABELS[field]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        )}
        {mode === "mentions" && (
          <div className="filter-group">
            <h4 className="filter-group-title">Quando</h4>
            <div className="filter-section">
              <div className="filter-section-head">
                <Calendar size={15} />
                <span>Período</span>
                {pin("period")}
              </div>
              <span className="filter-subtle-label">Início</span>
              <div className="drawer-fields">
                <input
                  type="date"
                  aria-label="Data inicial"
                  value={draft.dateFrom}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      dateFrom: event.target.value,
                    }))
                  }
                />
                <input
                  type="time"
                  aria-label="Hora inicial"
                  value={draft.timeFrom}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      timeFrom: event.target.value,
                    }))
                  }
                />
              </div>
              <span className="filter-subtle-label">Fim</span>
              <div className="drawer-fields">
                <input
                  type="date"
                  aria-label="Data final"
                  value={draft.dateTo}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      dateTo: event.target.value,
                    }))
                  }
                />
                <input
                  type="time"
                  aria-label="Hora final"
                  value={draft.timeTo}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      timeTo: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* As dimensões multi-seleção, agrupadas por ESCOPO. As de
            meio-escopo ficam separadas porque a pergunta que elas
            respondem só existe numa metade das menções — e desde
            2026-08-27 elas recortam só essa metade, o que o aviso
            de cada uma conta. */}
        <div className="filter-group">
          <h4 className="filter-group-title">O que foi dito</h4>
          <div className="filter-facets">
            {/* Polaridade mora na barra por padrão; aqui ela existe pra
                poder ser solta e fixada de novo (2026-10-30). */}
            {mode === "mentions" && facetFilter("polarities")}
            {(["approaches", "themes", "themeGroups", "keyMessages"] as const).map(
              (dimension) => facetFilter(dimension),
            )}
          </div>
          <div className="filter-section">
            <div className="filter-review">
              <ToolbarSelect<"all" | "yes" | "no">
                label="Subtema em destaque"
                ariaLabel="Subtema em destaque"
                align="right"
                value={draft.themeHighlight}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, themeHighlight: value }))
                }
                options={[
                  { value: "all", label: "Todas" },
                  { value: "yes", label: "Com destaque" },
                  { value: "no", label: "Sem destaque" },
                ]}
              />
              {pin("themeHighlight")}
            </div>
            <p className="filter-section-hint">
              O destaque escolhido à mão ou, sem escolha, o único subtema ou o
              de maior porcentagem. Empate sem escolha conta como sem.
            </p>
          </div>
          {mode === "mentions" && showsCena && (
            <div className="filter-section">
              <div className="filter-section-head">
                <Gauge size={15} />
                <span>CENA mínimo</span>
                {pin("minCena")}
              </div>
              <input
                type="number"
                aria-label="CENA mínimo"
                min={0}
                max={10}
                step={0.1}
                placeholder="Ex.: 6.0"
                value={draft.minCena}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    minCena: event.target.value,
                  }))
                }
              />
            </div>
          )}
        </div>

        <div className="filter-group">
          <h4 className="filter-group-title">Quem &amp; onde</h4>
          <div className="filter-facets">
            {(["brands", "sources", "spokespersons"] as const).map((dimension) =>
              facetFilter(dimension),
            )}
          </div>
          <div className="filter-section">
            <div className="filter-review">
              <ToolbarSelect<"all" | "yes" | "no">
                label="Post em mais de um cliente"
                ariaLabel="Post em mais de um cliente"
                align="right"
                value={draft.sharedPost}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, sharedPost: value }))
                }
                options={[
                  { value: "all", label: "Todos" },
                  { value: "yes", label: "Em mais de um" },
                  { value: "no", label: "Só neste" },
                ]}
              />
              {pin("sharedPost")}
            </div>
            <p className="filter-section-hint">
              O mesmo post também é menção de outra marca (o "também em" da
              linha), entre as menções que a tela mostra. Descartada não conta.
            </p>
          </div>
        </div>

        <div className="filter-group">
          <h4 className="filter-group-title">Só imprensa</h4>
          <div className="filter-facets">
            {(["vehicles", "mediaTypes", "tiers", "formats"] as const).map((dimension) =>
              facetFilter(dimension),
            )}
          </div>
          <div className="filter-section">
            <div className="filter-section-head">
              <CircleDollarSign size={15} />
              <span>Valoração mínima (R$)</span>
              {pin("minAdValue")}
            </div>
            <input
              type="number"
              aria-label="Valoração mínima em reais"
              // `min={1}` e não 0: "≥ R$ 0" não recorta nada, e o
              // servidor recusa. `step={1}` porque o recorte viaja
              // pro relatório, onde a trava por hash proíbe
              // fracionário — e o saneamento trunca o que passar.
              min={1}
              step={1}
              placeholder="Ex.: 5000"
              value={draft.minAdValue}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  minAdValue: event.target.value,
                }))
              }
            />
            <p className="filter-section-hint">
              Recorta a imprensa e deixa as menções de redes passarem —
              valoração é dado de imprensa.
            </p>
          </div>
          <div className="filter-section">
            <div className="filter-review">
              <ToolbarSelect<"all" | "yes" | "no">
                label="Citação no título"
                ariaLabel="Citação no título"
                align="right"
                value={draft.titleCitation}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, titleCitation: value }))
                }
                options={[
                  { value: "all", label: "Todas" },
                  { value: "yes", label: "Com citação" },
                  { value: "no", label: "Sem citação" },
                ]}
              />
              {pin("titleCitation")}
            </div>
            <p className="filter-section-hint">
              Só imprensa. TV e rádio não pontuam citação no título e contam
              como sem.
            </p>
          </div>
        </div>

        <div className="filter-group">
          <h4 className="filter-group-title">Só redes</h4>
          <div className="filter-facets">
            {(["channels", "influencerTypes", "publisherGroups"] as const).map(
              (dimension) => facetFilter(dimension),
            )}
          </div>
        </div>

        <div className="filter-group">
          <h4 className="filter-group-title">Revisão</h4>
          {mode === "mentions" && (
            <div className="filter-facets">
              {facetFilter(
                "editors",
                "Quem corrigiu a classificação à mão. Não viaja pra amostra.",
              )}
            </div>
          )}
          <div className="filter-review">
            <ToolbarSelect<"all" | "yes" | "no">
              label="Revisão"
              ariaLabel="Revisão"
              align="right"
              value={draft.reviewed}
              onChange={(value) =>
                setDraft((current) => ({ ...current, reviewed: value }))
              }
              options={[
                { value: "all", label: "Todas" },
                { value: "yes", label: "Só revisadas" },
                { value: "no", label: "Só não revisadas" },
              ]}
            />
              {pin("reviewed")}
          </div>
          {mode === "mentions" && (
            <div className="filter-review">
              <ToolbarSelect<"all" | "yes" | "no">
                label="Edição"
                ariaLabel="Edição"
                align="right"
                value={draft.edited}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, edited: value }))
                }
                options={[
                  { value: "all", label: "Todas" },
                  { value: "yes", label: "Só editadas" },
                  { value: "no", label: "Só não editadas" },
                ]}
              />
              {pin("edited")}
            </div>
          )}
          <p className="filter-section-hint">
            Entra no filtro salvo e vale pra amostra.
          </p>
          {mode === "mentions" && (
            <>
            <div className="filter-check-row">
            <label className="filter-check">
              <input
                type="checkbox"
                checked={draft.onlyDiscarded}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    onlyDiscarded: event.target.checked,
                  }))
                }
              />
              <span>Só as descartadas</span>
            </label>
            {pin("onlyDiscarded")}
            </div>
            <p className="filter-section-hint">
              Lista o que foi tirado da tela, pra restaurar. Não entra em
              amostra nem no filtro salvo.
            </p>
            </>
          )}
        </div>

        {/* Origem: a clipadora VIAJA pro relatório (nos dois modos); os
            uploads só aparecem quando a tela os oferece. */}
        <div className="filter-group">
          <h4 className="filter-group-title">Origem</h4>
          <div className="filter-facets">{facetFilter("clippingServices")}</div>
        {uploadOptions.length > 0 && onChangeUploadFilter && (
          <>
            <div className="filter-section">
              <div className="filter-section-head">
                <FileSpreadsheet size={15} />
                <span>Uploads</span>
                {pin("uploads")}
                {uploadFilter.length > 0 && (
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onChangeUploadFilter([])}
                  >
                    Limpar
                  </button>
                )}
              </div>
              <p className="filter-section-hint">
                Marcando mais de um, as menções dos selecionados aparecem
                juntas.
              </p>
              <div className="filter-chip-group">
                {uploadOptions.map((item) => {
                  const active = uploadFilter.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`filter-chip ${active ? "active" : ""}`}
                      aria-pressed={active}
                      title={uploadLabel(item)}
                      onClick={() =>
                        onChangeUploadFilter(
                          active
                            ? uploadFilter.filter(
                                (current) => current !== item.id,
                              )
                            : [...uploadFilter, item.id],
                        )
                      }
                    >
                      {active && <Check size={13} />}
                      <span className="filter-chip-label">
                        {uploadLabel(item)}
                      </span>
                      <span className="filter-chip-count">
                        {item.mention_count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
        </div>

        <div className="filter-group">
          <h4 className="filter-group-title">Links ou códigos colados</h4>
          <div className="filter-section">
            <div className="filter-section-head">
              <Link2 size={15} />
              <span>Lista colada</span>
              {pin("pastedRefs")}
              {refFilter && (
                <button
                  type="button"
                  className="text-button"
                  onClick={clearPastedReferences}
                >
                  Limpar
                </button>
              )}
            </div>
            <p className="filter-section-hint">
              Um por linha (vírgula e ponto-e-vírgula também separam).
              Casa pelo link, pelo link original ou pelo código da
              clipadora, e vale pra amostra. Até 20 mil; a lista
              fica guardada por uma semana.
            </p>
            <textarea
              rows={5}
              value={refDraft}
              onChange={(event) => setRefDraft(event.target.value)}
              placeholder={"https://…\ncódigo-da-notícia"}
              aria-label="Links ou códigos colados"
            />
            <div className="pasted-refs-actions">
              <button
                type="button"
                className="secondary-button"
                disabled={refBusy || refDraft.trim() === ""}
                onClick={() => void applyPastedReferences()}
              >
                {refBusy ? "Procurando…" : "Aplicar lista"}
              </button>
            </div>
            {refError && (
              <p className="pasted-refs-error" role="alert">
                {refError}
              </p>
            )}
            {refFilter && (
              <div className="pasted-refs-result">
                <strong>
                  {refFilter.postCount}{" "}
                  {refFilter.postCount === 1
                    ? "publicação encontrada"
                    : "publicações encontradas"}
                </strong>
                {refFilter.unmatchedCount > 0 && (
                  <>
                    <span>
                      {refFilter.unmatchedCount} sem par — confira a
                      grafia
                      {refFilter.unmatchedCount > refFilter.unmatched.length
                        ? ` (as primeiras ${refFilter.unmatched.length})`
                        : ""}
                      :
                    </span>
                    <ul>
                      {refFilter.unmatched.map((reference) => (
                        <li key={reference}>{reference}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="drawer-foot">
        {/* `onChangeUploadFilter` junto porque o contador inclui o
              filtro de upload — deixá-lo de fora faria "Limpar" parecer
              que não limpou tudo. */}
        <button
          className="secondary-button"
          disabled={activeFilterCount === 0 && !draftDiffers}
          onClick={() => {
            // O período volta pra janela padrão, não pra "tudo" (2026-10-26).
            void clearFilters();
            onChangeUploadFilter?.([]);
            clearPastedReferences();
          }}
        >
          Limpar filtros
        </button>
        <button className="primary-button" onClick={applyDraft}>
          <Check size={16} />
          Aplicar
        </button>
      </div>
    </aside>
  );
}
